import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { Subject, debounceTime, switchMap, map, of, merge } from 'rxjs';
import { IContact } from 'src/app/contact/models/contact';
import { ContactService } from 'src/app/contact/services/contact.service';
import { IBaseSearchRequest } from 'src/app/core/models/base';

@UntilDestroy()
@Component({
    selector: 'app-contact-combobox',
    templateUrl: './contact-combobox.component.html',
    styleUrls: ['./contact-combobox.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ContactComboboxComponent,
            multi: true,
        },
    ],
    standalone: false
})
export class ContactComboboxComponent implements OnInit {

  constructor(private contactService: ContactService) { 
    this.initData();
  }

  ngOnInit(): void {
  }

  @ViewChild(ComboBoxComponent, { static: true })
  kendoComboBox: ComboBoxComponent;

  @Input() disabled = false;
  @Input() readonly = false;
  @Input() valuePrimitive = true;
  @Input() set initialContact(value: IContact) {
    this.initialSub$.next(value);
    this._initialContact = value;
    this._value = value;
  }

  private searchSub$ = new Subject<IBaseSearchRequest>();
  private initialSub$ = new Subject<string | IContact>();
  public contacts: (IContact & { name: string })[] = [];
  private _initialContact: IContact;

  public _value: any;

  get value(): any {
    let copy = this._value;

    if (!this.valuePrimitive) {
      copy = { ...copy };
      delete copy.name;
    }

    return copy;
  }

  onChange: any = (value: any) => {};
  onTouch: any = () => {};

  writeValue(value: any): void {
    if (value === null) {
      this.kendoComboBox.reset();
    }

    this._value = value;

    if (this._initialContact) {
      return;
    }

    this.initialSub$.next(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: any) => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  initData() {
    const searchContact$ = this.searchSub$.pipe(
      debounceTime(300),
      switchMap((req) =>
        this.contactService.listContacts(req).pipe(
          map((response) => {
            return response.data;
          })
        )
      )
    );

    const initialContact$ = this.initialSub$.pipe(
      switchMap((value) => {
        if (!value) {
          return of([]);
        }

        if (typeof value === "string") {
          return this.contactService.getContact(value).pipe(
            map((response) => {
              return [response];
            })
          );
        } else {
          return of([value]);
        }
      })
    );

    merge(initialContact$, searchContact$)
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.contacts = data.map((contact) => {
          return {
            ...contact,
            name: contact.isCompany ? contact.companyName : contact.firstName + " " + contact.lastName,
          };
        });;
      });
  }

  onFilterChange(value) {
    if (value) {
      const request : IBaseSearchRequest = { keyword: value, pageNumber: 0, pageSize: 999999 };
      this.searchSub$.next(request);
    }
  }

  onValueChange(value) {
    if (value && !this.valuePrimitive) {
      value = { ...value };
      delete value.name;
    }

    if (!this.disabled) {
      this.onChange(value);
      this.onTouch(value);
      this._value = value;
    }
  }

  reset() {
    this.writeValue(null);
  }

}
